import { useEffect, useRef, useState, useCallback } from "react";
import { RefreshCw, X, Loader2 } from "lucide-react";

const VERSION_URL = "/version.json";

async function fetchVersion() {
  const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, { cache: "no-store" });
  if (!res.ok) throw new Error("version fetch failed");
  const data = await res.json();
  return data.version;
}

export function VersionChecker() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [busy, setBusy] = useState(false);
  const loadedRef = useRef(null);

  const check = useCallback(async () => {
    try {
      const v = await fetchVersion();
      if (!v) return;
      if (loadedRef.current == null) {
        loadedRef.current = v; // baseline = version at first load
        return;
      }
      if (v !== loadedRef.current) setUpdateAvailable(true);
    } catch {
      // offline of tijdelijke fout — negeren, geen storende melding
    }
  }, []);

  useEffect(() => {
    check();
    const onVisible = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", check);
    const interval = setInterval(check, 60000);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", check);
      clearInterval(interval);
    };
  }, [check]);

  const applyUpdate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // 1) Verwijder alle VRIJMARKT frontend-caches (auth blijft in HttpOnly-cookie).
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.filter((k) => k.startsWith("vrijmarkt")).map((k) => caches.delete(k)));
      }
      // 2) Activeer nieuwe SW / verwijder verouderde service workers.
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map(async (r) => {
          if (r.waiting) r.waiting.postMessage({ type: "SKIP_WAITING" });
          try { await r.unregister(); } catch { /* ignore */ }
        }));
      }
    } catch {
      // negeren — we herladen sowieso
    }
    // 3) Eén keer herladen met cache-busting query (geen reload-loop: baseline reset na reload).
    const url = new URL(window.location.href);
    url.searchParams.set("v", Date.now().toString());
    window.location.replace(url.toString());
  };

  if (!updateAvailable) return null;

  return (
    <div
      data-testid="version-update-banner"
      className="fixed inset-x-3 bottom-[76px] z-[60] mx-auto max-w-md rounded-2xl border border-[#FF6A00]/50 bg-[#1c1c1c] p-4 shadow-2xl shadow-black/60 md:bottom-4"
    >
      <div className="flex items-center gap-3">
        <RefreshCw className="h-5 w-5 shrink-0 text-[#FF6A00]" />
        <p className="flex-1 text-sm text-[#F5F5F5]">Er is een nieuwe versie van Vrijmarkt beschikbaar.</p>
        <button
          onClick={applyUpdate}
          disabled={busy}
          data-testid="version-update-btn"
          className="flex items-center gap-1.5 rounded-full bg-[#FF6A00] px-4 py-2 text-sm font-bold text-[#111] transition-all hover:bg-[#FFB27A] active:scale-95 disabled:opacity-60"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Nu bijwerken
        </button>
        <button
          onClick={() => setUpdateAvailable(false)}
          data-testid="version-dismiss-btn"
          aria-label="Sluiten"
          className="rounded-full p-1.5 text-[#A0A0A0] hover:text-[#F5F5F5]"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
